import type { ReactElement } from 'react'

import type { GetServerSideProps } from 'next'
import Link from 'next/link'

import isMobile from 'ismobilejs'

import { Container } from '@ui/components/Container'
import { Seo } from '@ui/components/seo'
import { HeroBasic } from '@ui/hero'
import { MarketingLayout } from '@ui/layout/MarketingLayout'

const title = 'Privacy Policy'
const description = ''

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  return { props: { userAgent: req.headers['user-agent'] ?? '' } }
}

export function Index({ userAgent }: { userAgent: string }) {
  const mobileInfo = isMobile(userAgent)
  const isMobilePhone = mobileInfo?.apple?.phone || mobileInfo?.android?.phone

  return (
    <>
      <Seo title={title} description={description} />
      <div className="relative bg-[url('/img/our-mission-bg.svg')] bg-cover bg-center pt-[120px]">
        <HeroBasic
          className="!bg-transparent"
          title={
            <span className="font-display tracking-wider text-[#0a2d4a]">
              {title.toUpperCase()}
            </span>
          }
          description="Effective Date: April 13, 2026"
        />
      </div>
      <Container>
        <div className="flex flex-col gap-6 py-20 text-[#0a2d4a]">
          <p>
            ATM.com, Inc., dba ATM.com® (referred to herein as “ATM”,
            “ATM.com”, “we”, “us” and “our”) knows that you care about how your
            personal information is used and shared.
          </p>
          <p>
            We want to provide you with answers to your questions and concerns
            regarding how your personal information is used. You may direct any
            privacy-related questions to{' '}
            <a
              href="mailto:legal@atm.com"
              className="text-blueberry-700 underline"
            >
              legal@atm.com
            </a>
            .
          </p>
          <p>
            This{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            describes ATM.com&apos;s practices regarding the collection, use,
            and disclosure of the information we collect from and about you when
            you use ATM.com&apos;s website (, or the “Website”), mobile
            application (“ATM.com” or the “Application” or the “App”), or
            associated products and services (collectively, with the Website and
            ATM.com, the “Services”). By accessing or otherwise using the
            Services, you consent to the collection, use, and disclosure of
            information as described in this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
          <h2
            id="toc"
            className="font-display text-2xl font-normal text-[#0a2d4a]"
          >
            Table of Contents
          </h2>
          <ol className="toc list-decimal pl-8">
            <li>
              <Link
                href="#the-information-we-collect"
                className="text-blueberry-700 underline"
              >
                THE INFORMATION WE COLLECT
              </Link>
            </li>
            <li>
              <Link
                href="#cookies-and-similar-technologies"
                className="text-blueberry-700 underline"
              >
                COOKIES AND SIMILAR TECHNOLOGIES
              </Link>
            </li>
            <li>
              <Link
                href="#how-we-use-your-information"
                className="text-blueberry-700 underline"
              >
                HOW WE USE YOUR INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#online-analytics-and-advertising"
                className="text-blueberry-700 underline"
              >
                ONLINE ANALYTICS AND ADVERTISING
              </Link>
            </li>
            <li>
              <Link
                href="#do-we-share-your-information"
                className="text-blueberry-700 underline"
              >
                DO WE SHARE YOUR INFORMATION?
              </Link>
            </li>
            <li>
              <Link
                href="#your-choices"
                className="text-blueberry-700 underline"
              >
                YOUR CHOICES
              </Link>
            </li>
            <li>
              <Link
                href="#third-party-links-and-services"
                className="text-blueberry-700 underline"
              >
                THIRD PARTY LINKS AND SERVICES
              </Link>
            </li>
            <li>
              <Link
                href="#eligibility"
                className="text-blueberry-700 underline"
              >
                ELIGIBILITY
              </Link>
            </li>
            <li>
              <Link
                href="#your-rights"
                className="text-blueberry-700 underline"
              >
                YOUR RIGHTS
              </Link>
            </li>
            <li>
              <Link
                href="#how-long-we-store-your-information"
                className="text-blueberry-700 underline"
              >
                HOW LONG WE STORE YOUR INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#changes-to-our-privacy-policy"
                className="text-blueberry-700 underline"
              >
                CHANGES TO OUR PRIVACY POLICY
              </Link>
            </li>
            <li>
              <Link
                href="#how-you-can-access-and-update-your-information"
                className="text-blueberry-700 underline"
              >
                HOW YOU CAN ACCESS AND UPDATE YOUR INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#data-security-how-we-protect-your-information"
                className="text-blueberry-700 underline"
              >
                DATA SECURITY; HOW WE PROTECT YOUR INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#california-privacy-rights"
                className="text-blueberry-700 underline"
              >
                CALIFORNIA PRIVACY RIGHTS
              </Link>
            </li>
            <li>
              <Link
                href="#use-of-the-services-from-outside-the-united-states"
                className="text-blueberry-700 underline"
              >
                USE OF THE SERVICES FROM OUTSIDE THE UNITED STATES
              </Link>
            </li>
            <li>
              <Link href="#contact-us" className="text-blueberry-700 underline">
                CONTACT US
              </Link>
            </li>
            <li>
              <Link
                href="#privacy-notice-for-california-residents"
                className="text-blueberry-700 underline"
              >
                PRIVACY NOTICE FOR CALIFORNIA RESIDENTS
              </Link>
            </li>
            <li>
              <Link
                href="#information-we-collect"
                className="text-blueberry-700 underline"
              >
                INFORMATION WE COLLECT
              </Link>
            </li>
            <li>
              <Link
                href="#use-of-personal-information"
                className="text-blueberry-700 underline"
              >
                USE OF PERSONAL INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#sharing-and-disclosure-of-personal-information-third-parties"
                className="text-blueberry-700 underline"
              >
                SHARING AND DISCLOSURE OF PERSONAL INFORMATION – THIRD PARTIES
              </Link>
            </li>
            <li>
              <Link
                href="#disclosures-of-personal-information-for-a-business-purpose-categories"
                className="text-blueberry-700 underline"
              >
                DISCLOSURES OF PERSONAL INFORMATION FOR A BUSINESS PURPOSE -
                CATEGORIES
              </Link>
            </li>
            <li>
              <Link
                href="#sales-of-personal-information"
                className="text-blueberry-700 underline"
              >
                SALES OF PERSONAL INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#your-rights-and-choices"
                className="text-blueberry-700 underline"
              >
                YOUR RIGHTS AND CHOICES
              </Link>
            </li>
            <li>
              <Link
                href="#deletion-request-rights"
                className="text-blueberry-700 underline"
              >
                DELETION REQUEST RIGHTS
              </Link>
            </li>
            <li>
              <Link
                href="#exercising-access-data-portability-and-deletion-rights"
                className="text-blueberry-700 underline"
              >
                EXERCISING ACCESS, DATA PORTABILITY, AND DELETION RIGHTS
              </Link>
            </li>
            <li>
              <Link
                href="#response-timing-and-format"
                className="text-blueberry-700 underline"
              >
                RESPONSE TIMING AND FORMAT
              </Link>
            </li>
            <li>
              <Link
                href="#personal-information-sales-opt-out-and-opt-in-rights"
                className="text-blueberry-700 underline"
              >
                PERSONAL INFORMATION SALES OPT-OUT and OPT-IN RIGHTS
              </Link>
            </li>
            <li>
              <Link
                href="#right-to-correct-inaccurate-information"
                className="text-blueberry-700 underline"
              >
                RIGHT TO CORRECT INACCURATE INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#limit-the-use-of-sensitive-personal-information"
                className="text-blueberry-700 underline"
              >
                LIMIT THE USE OF SENSITIVE PERSONAL INFORMATION
              </Link>
            </li>
            <li>
              <Link
                href="#notice-of-financial-incentive"
                className="text-blueberry-700 underline"
              >
                NOTICE OF FINANCIAL INCENTIVE
              </Link>
            </li>
            <li>
              <Link
                href="#appeal-rights"
                className="text-blueberry-700 underline"
              >
                APPEAL RIGHTS
              </Link>
            </li>
            <li>
              <Link
                href="#sharing-for-cross-context-behavioral-advertising"
                className="text-blueberry-700 underline"
              >
                SHARING FOR CROSS-CONTEXT BEHAVIORAL ADVERTISING
              </Link>
            </li>
            <li>
              <Link
                href="#note-for-residents-of-the-european-economic-area-united-kingdom-and-switzerland"
                className="text-blueberry-700 underline"
              >
                NOTE FOR RESIDENTS OF THE EUROPEAN ECONOMIC AREA, UNITED
                KINGDOM, AND SWITZERLAND
              </Link>
            </li>
            <li>
              <Link
                href="#changes-to-our-privacy-notice"
                className="text-blueberry-700 underline"
              >
                CHANGES TO OUR PRIVACY NOTICE
              </Link>
            </li>
            <li>
              <Link
                href="#contact-us-california"
                className="text-blueberry-700 underline"
              >
                CONTACT US
              </Link>
            </li>
          </ol>
          <h2
            id="the-information-we-collect"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            1. THE INFORMATION WE COLLECT
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.1 Information You Provide to Us
          </h3>
          <p>
            ATM.com collects information from you when you do certain things,
            such as:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Register for, administer, or sign into your account with ATM.com
              (&ldquo;ATM.com Account&ldquo;)
            </li>
            <li>Make a purchase</li>
            <li>Sign up to receive SMS (text message) notifications</li>
            <li>Download or use the ATM.com Application</li>
            <li>
              Use or otherwise interact with the Services directly or through
              social media
            </li>
            <li>Submit requests or questions to us via forms or email</li>
            <li>
              Communicate with us or have other dealings with us, including
              requests for customer support or technical assistance
            </li>
            <li>Submit uploads or posts to the Services</li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.2 Our “Data Messenger” Service
          </h3>
          <p>
            ATM.com helps subscribers earn money (“Rewards”) with our Services.
            One way subscribers earn money with our Services is by matching with
            a brand and opting in to receive messages from brands through the
            ATM.com application (“Data Messenger”). This is how our Data
            Messenger works. First, we compile information from you regarding
            your unique personal interests. Then, we link this information to a
            proprietary token that doesn&apos;t identify you to the outside
            world. We do not share any of your personal information with brands
            in this process; your personal information remains inside our secure
            vault (see below DATA SECURITY; HOW WE PROTECT YOUR INFORMATION).
            You may decide to opt-in to receiving in-app messages,
            notifications, and emails from any brand you may select through the
            Data Messenger.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.3 Types of Information
          </h3>
          <p>
            The types of information we collect will depend upon the Services
            you use, how you use them, and what you choose to provide. The types
            of data we collect directly from you may include:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Name, address, telephone number, email address, date of birth,
              SSN, and additional information collected for purposes of identity
              verification
            </li>
            <li>
              Optional information, including demographic information, or
              information that you elect to associate with your account (your
              “Profile Information”)
            </li>
            <li>
              Log-in details and password, if you create an ATM.com account
            </li>
            <li>Any email requests or questions you submit to us</li>
            <li>
              User content including but not limited to emails, communications,
              and other information for publication or distribution to third
              parties.
            </li>
          </ul>
          <p>
            If you choose to link a personal bank account (&ldquo;Bank
            Account&ldquo;) to your ATM.com user account within the Services, we
            will also request information from you such as your login
            credentials for those accounts and other financial data and
            documents that we receive from the financial institution.
          </p>
          <p>
            For users who want to access ATM Instant Cash Advances and related
            services, we may also collect the following types of bank account
            information from your linked bank:
          </p>
          <ul className="list-disc pl-6">
            <li>Balance information</li>
            <li>Transaction dates</li>
            <li>Merchant names</li>
            <li>Transaction types and amounts</li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.4 Information We Automatically Collect
          </h3>
          <p>
            When you use the Services, we may automatically collect certain
            information as described in this Section. As discussed further
            below, we and our service providers (which are third party companies
            that work on our behalf to provide and enhance the Services) use a
            variety of technologies, including cookies and similar tools, to
            assist in collecting this information.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.5 Log Files
          </h3>
          <p>
            When you use the Services, our servers automatically record certain
            information in server logs. These server logs may include
            information such as your web request, Internet Protocol (“IP”)
            address, browser type and settings, referring / exit pages and URLs,
            number of clicks and how you interact with the Services, metadata
            associated with uploaded Content, domain names, landing pages, pages
            viewed or moused-over, mobile carrier, date and time stamp
            information, and other such information.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.6 Device Identifiers
          </h3>
          <p>
            When you access the Services using a computer or mobile device, we
            may collect specific device information, including your MAC address
            and other unique device identifiers. We also collect information
            such as the type of device you are using, its operating system, and
            mobile network information, which may include your mobile phone
            number. We may associate this device identifier with your ATM.com
            Account and will use data associated with your device identifier to
            customize our Services to your device and to analyze any
            device-related issues. We may also gather information from your
            device to assist us with cross-device tracking, as described further
            below.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.7 Location Information
          </h3>
          <p>
            We may collect different types of information about your location,
            including general information (e.g., IP address, zip code) and more
            specific information (e.g., GPS-based functionality on mobile
            devices used to access the Services), and may use that information
            to customize the Services with location-based information,
            advertising, and features. For example, if your IP address indicates
            an origin in Los Angeles, the Services may be customized with Los
            Angeles- specific information and advertisements. If you access the
            Services through a mobile device and you do not want your device to
            provide us with location-tracking information, you can disable the
            GPS or other location-tracking functions on your device, provided
            your device allows you to do this. See your device
            manufacturer&apos;s instructions for further details. Even if you
            disable this, we may make certain assumptions about your location
            from your IP Address, or other information related to your use of
            the Services.
          </p>
          <p>
            In the App, if you voluntarily select to Enable Location Rewards,
            you will be given a choice to allow us to access your location one
            time or while using the App or not at all. If you authorize our
            access to your location data, we will share that data with
            third-party marketing partners, such as InMarket, in order to
            provide the services and features of Location Rewards.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.8 Information We Collect from Third-Party Integrations
          </h3>
          <p>
            If you choose to use third-party integrations through the Services,
            such as our transaction partners, such providers may allow us and
            our service providers to have access to and store additional
            information about your interaction with those services and platforms
            as it relates to use of the Services. As part of its Services to
            you, ATM.com monitors and analyzes all of your shared data.
          </p>
          <p>
            Additionally, you will also be subject to the Terms of Use and{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            of our marketing partners should you use their services, such as
            those from Adjoe -{' '}
            <a
              href="https://adjoe.zone/legal/en/terms_of_use.html"
              className="text-blueberry-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://adjoe.zone/legal/en/terms_of_use.html
            </a>
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.9 Third-Party Developers
          </h3>
          <p>
            The ATM App uses an SDK developed by Prodege, GmbH (“Prodege”).
            Prodege is an online offer platform, through which app users are
            exposed to market research surveys and/or third-party ads, including
            offers, games, shopping and ‘Magic Receipts’ (collectively,
            “Rewarded Activities”). Prodege collaborates with publishers of
            smartphone applications to provide their users with access to
            Rewarded Activities. When a user connects to this app, certain
            device and connection data (including Advertising ID, Device ID, and
            whether and how the user enabled access to the same) is
            automatically sent, via our app, to Prodege servers to enable
            Rewarded Activities to display through our app. Prodege collects and
            processes your data in accordance with applicable legal
            requirements. For a full list of data Prodege may receive through
            this app, and further information on how Prodege processes such
            data, see Prodege’s{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>
            , available at{' '}
            <a
              href="https://bitlabs.ai/offerwall-privacy-policy."
              className="text-blueberry-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://bitlabs.ai/offerwall-privacy-policy.
            </a>{' '}
            Prodege may transfer your data to non-EEA countries in accordance
            with applicable legal requirements, including the EU-US Data Privacy
            Framework and/or EU Commission Standard Contractual Clauses. Prodege
            may share such data with third parties, clients and business
            partners, for commercial purposes. By downloading the application,
            you accept the Prodege privacy policy and give your consent for
            Prodege to process such data. To exercise any applicable rights
            relating to your data, please visit:{' '}
            <a
              href="https://bitlabs.ai/offerwall-privacy-policy."
              className="text-blueberry-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://bitlabs.ai/offerwall-privacy-policy.
            </a>{' '}
            Again, please review the Prodege{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            for a more detailed view of how Prodege works and with whom Prodege
            shares data.
          </p>
          <p>
            The ATM website uses a web plugin developed by Prodege, GmbH
            (“Prodege”). Prodege is an online offer platform, through which
            users are exposed to market research surveys and/or third-party ads,
            including offers, games, shopping and ‘Magic Receipts’
            (collectively, “Rewarded Activities”). Prodege collaborates with
            publishers of smartphone applications and website owners to provide
            their users with access to Rewarded Activities. This website uses
            and enables Prodege cookies to serve Rewarded Activities. Prodege
            collects and processes your data in accordance with applicable legal
            requirements. For a full list of data Prodege may receive from this
            site, and further information on how Prodege processes such data,
            see Prodege’s{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>
            , available at{' '}
            <a
              href="https://bitlabs.ai/offerwall-privacy-policy."
              className="text-blueberry-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://bitlabs.ai/offerwall-privacy-policy.
            </a>{' '}
            Prodege may transfer your data to non-EEA countries in accordance
            with applicable legal requirements, including the EU-US Data Privacy
            Framework and/or EU Commission Standard Contractual Clauses. Prodege
            may share such data with third parties, clients and business
            partners, for commercial purposes. By using this website, you accept
            the Prodege privacy policy and give your consent for Prodege to
            process such data. To exercise any applicable rights relating to
            your data, please visit:{' '}
            <a
              href="https://bitlabs.ai/offerwall-privacy-policy."
              className="text-blueberry-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://bitlabs.ai/offerwall-privacy-policy.
            </a>{' '}
            Again, please review the Prodege{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            for a more detailed view of how Prodege works and with whom Prodege
            shares data.
          </p>
          <p>
            ATM.com partners with Cardlytics (“CDLX”) to provide earning
            opportunities (“Customer Incentives”) to you based on your banking
            transactions. CDLX will utilize Program Data and Customer Records in
            order to provide these Services to ATM.com, which will include, in
            part, data provided by Plaid, Inc. (“Plaid”). By agreeing to these
            Terms, you agree to the following: (1) ATM.com and CDLX may access
            your banking transaction data provided by Plaid; and (2) Your
            banking transaction data may be shared with and/or sold to third
            parties. You may withdraw your consent for sharing your bank
            transaction data with CDLX at any time in the app{' '}
            <a
              href={
                isMobilePhone
                  ? 'atmapp://app_navigator/?nav=opt_in&feature=cardlytics&opt_in_value=false'
                  : '/download'
              }
              className="text-blueberry-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              ({isMobilePhone ? `Open in App` : `Download`})
            </a>
            , at which point you will be removed from the Customer Incentive
            program.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.10 Information We Collect from Affiliates and Non-Affiliated Third
            Parties
          </h3>
          <p>
            ATM.com may receive additional information about you, such as
            demographic information, from third parties, such as business
            partners, marketers, researchers, analysts, and other parties that
            we may use to supplement the information that we collect directly
            from you.
          </p>
          <p>
            When you link your Bank Account to your ATM.com Account, ATM.com may
            also receive information about you and your Bank Account from the
            financial institution holding your Bank Account as necessary to
            provide the Services to you.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.11 Collection of Information Across Devices
          </h3>
          <p>
            Sometimes, we may use the information we collect -- for instance,
            usernames, IP addresses and unique mobile device identifiers - to
            locate or try to locate the same unique users across multiple
            browsers or devices (such as smartphones or tablets), or work with
            service providers that do this, in order to provide you with a
            seamless experience across devices.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.12 ATM.com Cashback
          </h3>
          <p>
            The ATM.com Cashback program (“Cashback”) is operated in conjunction
            with Cardlytics, Inc. (“Cardlytics”). When you enroll in Cashback
            and link your credit/debit card to the account, you are agreeing to
            Cardlytics collecting transaction information from your linked
            account, which Cardlytics will retain as long as you are enrolled in
            the program. When your enrollment terminates, Cardlytics will
            promptly delete any personal identifiers included in the transaction
            data to maintain your privacy. We may combine transaction data from
            this program with data from other rewards programs Cardlytics
            operates to avoid duplication of rewards. Cardlytics will use your
            transaction data for purposes of operating the ATM.com Cashback
            feature for campaign targeting, reporting and measurement.
            Cardlytics may sell anonymized and aggregated transaction data to
            advertisers as part of its marketing services in the form of
            campaign reporting, measurement, and insights. Transaction-level
            information may be shared with advertisers for validation purposes
            only.
          </p>
          <h2
            id="cookies-and-similar-technologies"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            2. COOKIES AND SIMILAR TECHNOLOGIES
          </h2>
          <p>
            To collect the information in the “Information We Automatically
            Collect” section above, we and our service providers use Internet
            server logs, cookies, tags, SDKs, tracking pixels, and other similar
            tracking technologies. A web server log is a file where website
            activity is stored. An SDK is a section of code that we embed in our
            applications and software to allow third parties to collect
            information about how users interact with the Services. A cookie is
            a small text file that is placed on your computer or mobile device
            when you visit a site, that enables us to: (i) recognize your
            computer; (ii) store your preferences and settings; (iii) understand
            which web pages of the Services you have visited; (iv), enhance your
            user experience by delivering and measuring the effectiveness of
            content and advertising tailored to your interests; (v) perform
            analytics; and (vi) assist with security and administrative
            functions. Tracking pixels (sometimes referred to as web beacons or
            clear GIFs) are tiny electronic tags with a unique identifier
            embedded in websites, online ads and/or email, and that are designed
            to provide usage information like ad impressions or clicks, email
            open rates, measure popularity of the Services and associated
            advertising, and to access user cookies. As we adopt additional
            technologies, we may also gather information through other methods.
          </p>
          <p>
            We partner with Microsoft Clarity and Microsoft Advertising to
            capture how you use and interact with our website through behavioral
            metrics, heatmaps, and session replay to improve and market our
            products/services. Website usage data is captured using first and
            third-party cookies and other tracking technologies to determine the
            popularity of products/services and online activity. Additionally,
            we use this information for site optimization, fraud/security
            purposes, and advertising. For more information about how Microsoft
            collects and uses your data, visit the{' '}
            <a
              href="https://www.microsoft.com/en-us/privacy/privacystatement"
              className="text-blueberry-700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Microsoft Privacy Statement
            </a>
            .
          </p>
          <p>
            Most browsers automatically accept cookies, but you can modify your
            browser setting to decline cookies by visiting the Help portion of
            your browser toolbar (e.g., Internet Explorer; Google Chrome;
            Mozilla Firefox; or Apple Safari). If you choose to decline cookies,
            please note that you may not be able to sign in, customize, or use
            some of the interactive features of the Services. Flash cookies
            operate differently than browser cookies, and cookie management
            tools available in a web browser will not remove Flash cookies.
          </p>
          <h2
            id="how-we-use-your-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            3. HOW WE USE YOUR INFORMATION
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            3.1 Use
          </h3>
          <p>
            We use your information (including any information that we collect,
            as described in this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>
            ) for various purposes depending on the types of information we have
            collected from and about you and the specific ATM.com Services you
            use, including to:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Provide you the opportunity to earn money with our Data Messenger
              service
            </li>
            <li>
              Provide the services and customer support you have requested or
              subscribed to
            </li>
            <li>
              Provide the highest earning opportunities with our Paid Survey
              service
            </li>
            <li>
              Provide earning opportunities tailored specifically to your
              location using our Location Rewards service
            </li>
            <li>
              Process transactions and send notices about your transactions or
              your network activity
            </li>
            <li>Compare information for accuracy and verification purposes</li>
            <li>
              Respond to your request for information and provide you with more
              effective and efficient customer service
            </li>
            <li>
              Provide you with product and service updates that we are offering
            </li>
            <li>
              Provide you with service notifications via email and within the
              Services based on your notification selections
            </li>
            <li>Customize the advertising and content you see</li>
            <li>
              Synthesize and derive insights from your use of the Services to
              help us better understand your interests and needs, and improve
              the services
            </li>
            <li>Protect our Services and our users</li>
            <li>
              Enforce the legal terms that govern your use of the Services
            </li>
          </ul>
          <p>
            If you have elected an ATM Instant Cash Advance, we may also use
            your information for our computer analysis to determine if your
            request for an ATM Instant Cash Advance will be approved.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            3.2 User Content
          </h3>
          <p>
            The Services may permit you to submit content, send emails and other
            communications, and provide other information for publication or
            distribution to third parties (collectively, &ldquo;User
            Content&ldquo;). ATM.com may view and share your User Content for
            any reason, including: (i) to maintain, provide and improve the
            Service; (ii) to resolve a support request from you; (iii) if we
            have a good faith belief, or have received a complaint alleging,
            that such Content is in violation of our Terms of Service; (iv) as
            reasonably necessary to allow ATM.com to comply with or avoid the
            violation of applicable law or regulation; or (v) to comply with a
            valid legal subpoena, request, or other lawful process. We may also
            analyze your User Content in order to better understand the manner
            in which our Service is being used.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            3.3 Combined Information
          </h3>
          <p>
            For the purposes discussed in this Policy, we may combine the
            information that we collect through the Services with information
            that we receive from other sources, both online and offline, and use
            such combined information in accordance with this Policy.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            3.4 De-Identified and Aggregated Information
          </h3>
          <p>
            We may de-identify and aggregate information collected through the
            Services from our subscribers so that such information can no longer
            be linked to you or your device. We may (i) monetize this
            information collected as survey data and share the results in part
            with our subscribers, and (ii) use it for purposes of market or
            scientific research.
          </p>
          <h2
            id="online-analytics-and-advertising"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            4. ONLINE ANALYTICS AND ADVERTISING
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.1 Analytics
          </h3>
          <p>
            We use third-party web analytics services on our Services to collect
            and analyze some of the information discussed above, and to engage
            in auditing, research or reporting. The information (including your
            IP address) collected by various analytics technologies described in
            the “Cookies and Similar Technologies” section will be disclosed to
            or collected directly by these service providers, who use the
            information to evaluate your use of the Services, including by
            noting the third-party website from which you arrive, analyzing
            usage trends across ATM.com websites and mobile devices, assisting
            with fraud prevention, and providing certain features to you.
          </p>
          <p>
            If you receive email from us, we may use certain analytics tools to
            capture data, such as when you open our message or click on any
            links or banners our email contains. This data allows us to gauge
            the effectiveness of our communications and marketing campaigns.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.2 Online Advertising
          </h3>
          <p>
            Third parties or affiliates may administer ATM.com banner
            advertising programs and other online marketing on non-ATM.com
            websites and services. To do so, these parties may set and access
            first-party cookies delivered from an ATM.com domain, or they may
            use third-party cookies or other tracking mechanisms. For example, a
            third-party provider may use the fact that you visited the ATM
            website to target online ads (retargeted ads) for ATM.com services
            to you on non-ATM.com websites or across mobile apps. Or a third-
            party ad network might collect information on the Services and other
            websites to develop a profile of your interests and target
            advertisements to you based on your online behavior (retargeted
            ads). These parties that use these technologies may offer you a way
            to opt out of ad targeting as described below. You may receive
            retargeted ads on your computer through a web browser.
          </p>
          <p>
            If you are interested in more information about tailored browser
            advertising and how you can generally control cookies from being put
            on your computer to deliver tailored advertising, you may visit the
            , the , or to opt-out of receiving tailored advertising from
            companies that participate in those programs. To opt out of Google
            Analytics for display advertising or customize Google display
            network ads, you can visit the . Please note that we do not control
            any of the above opt-out links or whether any particular company
            chooses to participate in these opt-out programs. We are not
            responsible for any choices you make using these mechanisms or the
            continued availability or accuracy of these mechanisms
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.3 California Do-Not-Track Disclosure Requirements
          </h3>
          <p>
            We are committed to providing you with meaningful choices about the
            information collected on our Services for third-party purposes, and
            that is why we provide above the Network Advertising Initiatives
            Consumer Opt-out link, Digital Advertising Alliances Consumer
            Opt-Out Link, TRUSTe&apos;s Advertising Choices page, the Google
            opt-out link. However, we do not recognize or respond to
            browser-initiated Do Not Track signals, as the Internet industry is
            currently still working on Do Not Track standards, implementations
            and solutions.
          </p>
          <h2
            id="do-we-share-your-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            5. DO WE SHARE YOUR INFORMATION?
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.1 Affiliates and Subsidiaries
          </h3>
          <p>
            We may share your information with our affiliated entities such as
            subsidiaries. Affiliated entities are entities that we legally
            control (by voting rights) or that control us.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.2 Service Providers
          </h3>
          <p>
            We may provide access to or share your information with select third
            parties who perform services on our behalf. These third parties
            provide a variety of services to us, including without limitation,
            billing, customer service, data storage, security, fraud prevention,
            payment processing through the Automated Clearing House Network, and
            legal services.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.3 Legal Process
          </h3>
          <p>
            We may disclose your information in response to any subpoenas, court
            orders, or other legal process we receive, or to establish or
            exercise our legal rights or to defend against legal claims.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.4 Investigations
          </h3>
          <p>
            We may disclose your information when we believe in good faith that
            such disclosure is appropriate in order to investigate, prevent, or
            take action regarding possible illegal activities, suspected fraud,
            situations involving potential threats to the physical safety of any
            person, violations of this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            or the Terms of Service for the Services you accessed, and/or to
            protect the rights and property of ATM.com, our employees, users,
            and the public. This may involve the sharing of your information
            with law enforcement, government agencies, courts, and/or other
            organizations.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.5 Third-Party Integrations
          </h3>
          <p>
            When you initiate a connection with a third-party integration
            through the Services, we will share information about you that is
            required to enable your use of the third-party integration through
            the Services. These third parties may use the location and device
            data, as well as information you voluntarily submit to them, for
            targeted advertising, measurement and analytics.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.6 Business Transfers
          </h3>
          <p>
            If the ownership of all or substantially all of our business
            changes, we may transfer your information to the new owner so that
            the Services can continue to operate. In such case, your information
            would remain subject to the promises and commitments contained in
            this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            until such time as this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            is updated or amended by the acquiring party upon notice to you. If
            such transfer is subject to additional mandatory restrictions under
            applicable laws, ATM.com will comply with such restrictions.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.7 De-Identified and Aggregated Information
          </h3>
          <p>
            As stated above, we may share and monetize De-Identified and
            Aggregated Information collected as survey data from our
            subscribers.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            5.8 Consent
          </h3>
          <p>
            We may also disclose your information to third parties with your
            prior consent.
          </p>
          <h2
            id="your-choices"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            6. YOUR CHOICES
          </h2>
          <p>
            We provide you with a number of choices with respect to the
            information we collect and use as discussed throughout this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>
            . For example, you may instruct us not to use your contact
            information to contact you by email, postal mail or phone regarding
            products, services, promotions and special events that might appeal
            to your interests by contacting us. In commercial email messages,
            you can also opt out by following the instructions located at the
            bottom of such emails. Please note that, regardless of your request,
            we may still use and share certain information as permitted by this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            or as required by applicable law. For example, you may not opt out
            of certain operational or service-related emails, such as those
            reflecting our relationship or transactions with you.
          </p>
          <h2
            id="third-party-links-and-services"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            7. THIRD PARTY LINKS AND SERVICES
          </h2>
          <p>
            The Services contain links to third-party apps or websites or
            services, and also contain third-party integrations. If you choose
            to use these, you may disclose your information not just to those
            third parties, but also to their users and the public more generally
            depending on how their services function. Because these third-party
            websites and services are not operated by ATM.com, ATM.com is not
            responsible for the content or practices of those websites or
            services. The collection, use, and disclosure of your personal and
            other information will be subject to the privacy policies of the
            third- party websites or services, and not this Policy. We urge you
            to read the privacy and security policies of these third parties.
          </p>
          <h2
            id="eligibility"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            8. ELIGIBILITY
          </h2>
          <p>
            The Services are not intended for individuals under the age of 18.
            We do not knowingly solicit or collect information from individuals
            under the age of 18.
          </p>
          <h2
            id="your-rights"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            9. YOUR RIGHTS
          </h2>
          <p>
            If you want to learn more about the information collected through
            the Services, or if you would like to access or rectify your
            information and/or request deletion of information we collect about
            you, or restrict or object to the processing of your information,
            please contact us using the contact information below.
          </p>
          <p>
            Where you have provided consent, you may withdraw your consent at
            any time, without affecting the lawfulness of the processing that
            was carried out prior to withdrawing your consent.
          </p>
          <h2
            id="how-long-we-store-your-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            10. HOW LONG WE STORE YOUR INFORMATION
          </h2>
          <p>
            We will retain your information for the period necessary to fulfill
            the purposes outlined in this Policy unless a longer retention
            period is required or permitted by law.
          </p>
          <h2
            id="changes-to-our-privacy-policy"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            11. CHANGES TO OUR PRIVACY POLICY
          </h2>
          <p>
            We reserve the right to amend this Policy at any time to reflect
            changes in the law, our data collection and use practices, the
            features of our Services, or advances in technology. We will make
            the revised Policy accessible through the Services, so you should
            review the Policy periodically. If we make a material change to the
            Policy, you will be provided with appropriate notice and we will
            seek your consent to the updated Policy in accordance with legal
            requirements.
          </p>
          <h2
            id="how-you-can-access-and-update-your-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            12. HOW YOU CAN ACCESS AND UPDATE YOUR INFORMATION
          </h2>
          <p>
            To access, review, update and/or delete certain user profile
            information, you may update and/or delete certain user profile
            information by logging into the Website and updating your ATM.com
            Account. Please remember, however, if we have already disclosed some
            of this user profile information to third parties, we cannot force
            the deletion or modification of any such user profile information by
            the parties to whom we have made those disclosures.
          </p>
          <h2
            id="data-security-how-we-protect-your-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            13. DATA SECURITY; HOW WE PROTECT YOUR INFORMATION
          </h2>
          <p>
            We will implement reasonable and appropriate security procedures
            consistent with prevailing industry standards to protect information
            from unauthorized access by physical and electronic intrusion.
          </p>
          <p>
            Unfortunately, no data transmission over the Internet or method of
            data storage can be guaranteed 100% secure. Therefore, while we
            strive to protect your information by following generally accepted
            industry standards, we cannot ensure or warrant the absolute
            security of any information you transmit to us or archive at this
            site. If you have any questions about how we protect your
            information, you can contact us at{' '}
            <a
              href="mailto:legal@atm.com"
              className="text-blueberry-700 underline"
            >
              legal@atm.com
            </a>
            .
          </p>
          <h2
            id="california-privacy-rights"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            14. CALIFORNIA PRIVACY RIGHTS
          </h2>
          <p>
            California law permits users who are California residents to request
            and obtain from us once a year, free of charge, a list of the third
            parties to whom we have disclosed their personal information (if
            any) for their direct marketing purposes in the prior calendar year,
            as well as the type of personal information disclosed to those
            parties. If you are a California resident and would like to request
            this information, please submit your request in an email to{' '}
            <a
              href="mailto:legal@atm.com"
              className="text-blueberry-700 underline"
            >
              legal@atm.com
            </a>
            .
          </p>
          <p>
            Additionally, California law permits residents under the age of 18
            who are registered users with us to request and obtain removal of
            content or information they have publicly posted on our Services. To
            make such a request, please send an email with a detailed
            description of the specific content or information to{' '}
            <a
              href="mailto:legal@atm.com"
              className="text-blueberry-700 underline"
            >
              legal@atm.com
            </a>
            . Please be aware that such a request does not ensure complete or
            comprehensive removal of the content or information you have posted
            and that there may be circumstances in which the law does not
            require or allow removal even if requested.
          </p>
          <h2
            id="use-of-the-services-from-outside-the-united-states"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            15. USE OF THE SERVICES FROM OUTSIDE THE UNITED STATES
          </h2>
          <p>
            The Services are provided, supported, and hosted in the United
            States and are governed by United States law and the laws of the
            applicable states within the United States. If you are using the
            Services from outside the United States, please be aware that your
            information may be transferred to, stored and processed in the
            United States where our servers are located and our databases are
            operated. The data protection and other laws of the United States
            might not be as comprehensive as those in your country. By using the
            Services, you consent to your information being transferred to our
            facilities and to the facilities of those third parties with whom we
            share it as described in our{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
          <h2
            id="contact-us"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            16. CONTACT US
          </h2>
          <p>
            If you wish to contact us or have any questions about or complaints
            in relation to this{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>
            , please contact us at{' '}
            <a
              href="mailto:legal@atm.com"
              className="text-blueberry-700 underline"
            >
              legal@atm.com
            </a>
            , or mail to ATM.com, Inc., P.O. Box 7475, Newport Beach, CA
            92658-9998.
          </p>
          <h2
            id="privacy-notice-for-california-residents"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            17. PRIVACY NOTICE FOR CALIFORNIA RESIDENTS
          </h2>
          <p>Effective Date: September 25, 2020</p>
          <p>Last Reviewed on August 1, 2025</p>
          <p>
            This Privacy Notice for California Residents supplements the
            information contained in the ATM.com and applies solely to all
            visitors, users, and others who reside in the State of California
            (&ldquo;consumers&ldquo; or &ldquo;you&ldquo;). ATM.com, Inc. (“we”,
            “our” or “us”) adopts this notice to comply with the California
            Consumer Privacy Act of 2018 (“CCPA”) and any terms defined in the
            CCPA have the same meaning when used in this notice.
          </p>
          <h2
            id="information-we-collect"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            18. INFORMATION WE COLLECT
          </h2>
          <p>
            Our website collects information that identifies, relates to,
            describes, references, is capable of being associated with, or could
            reasonably be linked, directly or indirectly, with a particular
            consumer or device (&ldquo;Personal Information&ldquo;).
          </p>
          <p>
            In particular, we indicate below the categories of Personal
            Information collected from California residents within the last
            twelve (12) months.
          </p>
          <div>
            <p>CATEGORY A: Identifiers</p>
            <p>
              Examples: A real name, Internet Protocol address, email address,
              or other similar identifiers.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>
              CATEGORY B: Personal information categories listed in the
              California Customer Records statute (Cal. Civ. Code § 1798.80(e)).
            </p>
            <p>
              Examples: A name, signature, Social Security number, physical
              characteristics or description, address, telephone number,
              passport number, driver&apos;s license or state identification
              card number, insurance policy number, education, employment,
              employment history, bank account number, credit card number, debit
              card number, or any other financial information, medical
              information, or health insurance information. Some Personal
              Information included in this category may overlap with other
              categories.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>
              CATEGORY C: Protected classification characteristics under
              California or federal law.
            </p>
            <p>
              Examples: Age (40 years or older), race, color, ancestry, national
              origin, citizenship, religion or creed, marital status, medical
              condition, physical or mental disability, sex (including gender,
              gender identity, gender expression, pregnancy or childbirth and
              related medical conditions), sexual orientation, veteran or
              military status, genetic information (including familial genetic
              information).
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>CATEGORY D: Commercial information.</p>
            <p>
              Examples: Records of personal property, products or services
              purchased, obtained, or considered, or other purchasing or
              consuming histories or tendencies.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>CATEGORY E: Biometric information.</p>
            <p>
              Examples: Genetic, physiological, behavioral, and biological
              characteristics, or activity patterns used to extract a template
              or other identifier or identifying information, such as,
              fingerprints, faceprints, and voiceprints, iris or retina scans,
              keystroke, gait, or other physical patterns, and sleep, health, or
              exercise data.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>CATEGORY F: Internet or other similar network activity.</p>
            <p>
              Examples: Browsing history, search history, information on a
              consumer&apos;s interaction with a website, application, or
              advertisement.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>CATEGORY G: Geolocation data.</p>
            <p>Examples: Physical location or movements.</p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>CATEGORY H: Sensory data.</p>
            <p>
              Examples: Audio, electronic, visual, thermal, olfactory, or
              similar information.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>CATEGORY I: Professional or employment-related information.</p>
            <p>
              Examples: Current or past job history or performance evaluations.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>
              CATEGORY J: Non-public education information (per the Family
              Educational Rights and Privacy Act (20 U.S.C. Section 1232g, 34
              C.F.R. Part 99)).
            </p>
            <p>
              Examples: Education records directly related to a student
              maintained by an educational institution or party acting on its
              behalf, such as grades, transcripts, class lists, student
              schedules, student identification codes, student financial
              information, or student disciplinary records.
            </p>
            <p>Collected: YES</p>
          </div>
          <div>
            <p>CATEGORY K: Inferences drawn from other Personal Information.</p>
            <p>
              Examples: Profile reflecting a person&apos;s preferences,
              characteristics, psychological trends, predispositions, behavior,
              attitudes, intelligence, abilities, and aptitudes.
            </p>
            <p>Collected: YES</p>
          </div>
          <p>Personal information does not include:</p>
          <ul className="list-disc pl-6">
            <li>Publicly available information from government records.</li>
            <li>Deidentified or aggregated consumer information.</li>
            <li>
              Information excluded from the CCPA&apos;s scope, like:
              <ul className="list-[circle] pl-6">
                <li>
                  Health or medical information covered by the Health Insurance
                  Portability and Accountability Act of 1996 (HIPAA) and the
                  California Confidentiality of Medical Information Act (CMIA)
                  or clinical trial data;
                </li>
                <li>
                  Personal Information covered by certain sector-specific
                  privacy laws, including the Fair Credit Reporting Act (FRCA),
                  the Gramm- Leach-Bliley Act (GLBA) or California Financial
                  Information Privacy Act (FIPA), and the Driver&apos;s Privacy
                  Protection Act of 1994.
                </li>
              </ul>
            </li>
          </ul>
          <p>
            We collect the categories of Personal Information listed above from
            the following categories of sources:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Directly from you (for example, from forms you complete or
              products and services you purchase).
            </li>
            <li>
              Indirectly from you (for example, from observing your actions on
              our website).
            </li>
            <li>
              Otherwise as described in our{' '}
              <Link
                href="/privacy-policy"
                className="text-blueberry-700 underline"
              >
                Privacy Policy
              </Link>
              .
            </li>
          </ul>
          <h2
            id="use-of-personal-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            19. USE OF PERSONAL INFORMATION
          </h2>
          <p>
            We may use, or disclose the Personal Information we collect for one
            or more of the following business purposes (a business purpose is
            defined as “the use of Personal Information for the business&apos;s
            or a service provider&apos;s operational purposes, or other notified
            purposes, provided that the use of Personal Information shall be
            reasonably necessary and proportionate to achieve the operational
            purpose for which the Personal Information was collected or
            processed or for another operational purpose that is compatible with
            the context in which the Personal Information was collected”):
          </p>
          <ul className="list-disc pl-6">
            <li>
              To fulfill or meet the reason you provided the information. For
              example, if you share your name and contact information to request
              a price quote or ask a question about our products or services, we
              will use that Personal Information to respond to your inquiry. If
              you provide your Personal Information to purchase a product or
              service, we will use that information to process your payment and
              facilitate delivery. We may also save your information to
              facilitate new product orders or process returns.
            </li>
            <li>
              To provide, support, personalize, and develop our website,
              products, and services.
            </li>
            <li>
              To create, maintain, customize, and secure your account with us.
            </li>
            <li>
              To process your requests, purchases, transactions, and payments
              and prevent transactional fraud.
            </li>
            <li>
              To provide you with support and to respond to your inquiries,
              including to investigate and address your concerns and monitor and
              improve our responses.
            </li>
            <li>
              To personalize your website experience and to deliver content and
              product and service offerings relevant to your interests,
              including targeted offers and retargeted ads through our website,
              third-party sites, and via email or text message (with your
              consent, where required by law).
            </li>
            <li>
              To help maintain the safety, security, and integrity of our
              website, products and services, databases and other technology
              assets, and business.
            </li>
            <li>
              For testing, research, analysis, and product development,
              including to develop and improve our website, products, and
              services.
            </li>
            <li>
              To respond to law enforcement requests and as required by
              applicable law, court order, or governmental regulations.
            </li>
            <li>
              As described to you when collecting your Personal Information or
              as otherwise set forth in the CCPA.
            </li>
            <li>
              To evaluate or conduct a merger, divestiture, restructuring,
              reorganization, dissolution, or other sale or transfer of some or
              all of our assets, whether as a going concern or as part of
              bankruptcy, liquidation, or similar proceeding, in which Personal
              Information held by us about our website users is among the assets
              transferred.
            </li>
            <li>
              Otherwise as described in our{' '}
              <Link
                href="/privacy-policy"
                className="text-blueberry-700 underline"
              >
                Privacy Policy
              </Link>
              , including without limitation, engagement in retargeted ads via
              ad networks.
            </li>
          </ul>
          <p>
            We will not collect additional categories of Personal Information or
            use the Personal Information we collected for materially different,
            unrelated, or incompatible purposes without providing you notice.
          </p>
          <h2
            id="sharing-and-disclosure-of-personal-information-third-parties"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            20. SHARING AND DISCLOSURE OF PERSONAL INFORMATION – THIRD PARTIES
          </h2>
          <p>
            We may share your personal information as indicated above in our{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            (see above DO WE SHARE YOUR INFORMATION?).
          </p>
          <h2
            id="disclosures-of-personal-information-for-a-business-purpose-categories"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            21. DISCLOSURES OF PERSONAL INFORMATION FOR A BUSINESS PURPOSE -
            CATEGORIES
          </h2>
          <p>
            In the preceding twelve (12) months, Company has disclosed the
            following categories of Personal Information for a business purpose:
          </p>
          <ul className="list-disc pl-6">
            <li>Category A: Identifiers.</li>
            <li>
              Category B: California Customer Records Personal Information
              categories.
            </li>
            <li>
              Category C: Protected classification characteristics under
              California or federal law.
            </li>
            <li>Category D: Commercial information.</li>
            <li>Category E: Biometric information.</li>
            <li>Category F: Internet or other similar network activity.</li>
            <li>Category G: Geolocation data.</li>
            <li>Category H: Sensory data.</li>
            <li>Category I: Professional or employment-related information</li>
            <li>
              Category J: Non-public education information (per the Family
              Educational Rights and Privacy Act (20 U.S.C. Section 1232g, 34
              C.F.R. Part 99)).
            </li>
            <li>
              Category K: Inferences drawn from other Personal Information
            </li>
          </ul>
          <h2
            id="sales-of-personal-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            22. SALES OF PERSONAL INFORMATION
          </h2>
          <p>
            In the preceding twelve (12) months, we have not sold Personal
            Information to data brokers, aggregators, or to other third parties
            with transactions involving the transfer of our lists for monetary
            compensation. We have engaged in retargeted ads via ad networks; to
            opt out, visit our{' '}
            <Link
              href="/privacy-policy"
              className="text-blueberry-700 underline"
            >
              Privacy Policy
            </Link>{' '}
            above (See ONLINE ANALYTICS AND ADVERTISING).
          </p>
          <h2
            id="your-rights-and-choices"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            23. YOUR RIGHTS AND CHOICES
          </h2>
          <p>
            The CCPA provides consumers (California residents) with specific
            rights regarding their Personal Information. This section describes
            your CCPA rights and explains how to exercise those rights.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            23.1 Access to Specific Information and Data Portability Rights
          </h3>
          <p>
            You have the right to request that we disclose certain information
            to you about our collection and use of your Personal Information
            over the past 12 months. Once we receive and confirm your verifiable
            consumer request (see EXERCISING ACCESS, DATA PORTABILITY, AND
            DELETION RIGHTS), we will disclose to you:
          </p>
          <ul className="list-disc pl-6">
            <li>
              The categories of Personal Information we collected about you.
            </li>
            <li>
              The categories of sources for the Personal Information we
              collected about you.
            </li>
            <li>
              Our business or commercial purpose for collecting or selling that
              Personal Information.
            </li>
            <li>
              The categories of third parties with whom we share that Personal
              Information.
            </li>
            <li>
              The specific pieces of Personal Information we collected about you
              (also called a data portability request).
            </li>
            <li>
              If we sold or disclosed your Personal Information for a business
              purpose, two separate lists disclosing:
              <ul className="list-[circle] pl-6">
                <li>
                  sales, identifying the Personal Information categories that
                  each category of recipient purchased; and
                </li>
                <li>
                  disclosures for a business purpose, identifying the Personal
                  Information categories that each category of recipient
                  obtained.
                </li>
              </ul>
            </li>
          </ul>
          <h2
            id="deletion-request-rights"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            24. DELETION REQUEST RIGHTS
          </h2>
          <p>
            You have the right to request that we delete any of your Personal
            Information that we collected from you and retained, subject to
            certain exceptions. Once we receive and confirm your verifiable
            consumer request (see EXERCISING ACCESS, DATA PORTABILITY, AND
            DELETION RIGHTS below), we will delete (and direct our service
            providers to delete) your Personal Information from our records,
            unless an exception applies.
          </p>
          <p>
            We may deny your deletion request if retaining the information is
            necessary for us or our service provider(s) to:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Complete the transaction for which we collected the Personal
              Information, provide a good or service that you requested, take
              actions reasonably anticipated within the context of our ongoing
              business relationship with you, or otherwise perform our contract
              with you.
            </li>
            <li>
              Detect security incidents, protect against malicious, deceptive,
              fraudulent, or illegal activity, or prosecute those responsible
              for such activities.
            </li>
            <li>
              Debug products to identify and repair errors that impair existing
              intended functionality.
            </li>
            <li>
              Exercise free speech, ensure the right of another consumer to
              exercise their free speech rights, or exercise another right
              provided for by law.
            </li>
            <li>
              Comply with the California Electronic Communications Privacy Act
              (Cal. Penal Code § 1546 et. seq.).
            </li>
            <li>
              Engage in public or peer-reviewed scientific, historical, or
              statistical research in the public interest that adheres to all
              other applicable ethics and privacy laws, when the
              information&apos;s deletion may likely render impossible or
              seriously impair the research&apos;s achievement, if you
              previously provided informed consent.
            </li>
            <li>
              Enable solely internal uses that are reasonably aligned with
              consumer expectations based on your relationship with us.
            </li>
            <li>Comply with a legal obligation.</li>
            <li>
              Make other internal and lawful uses of that information that are
              compatible with the context in which you provided it.
            </li>
          </ul>
          <h2
            id="exercising-access-data-portability-and-deletion-rights"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            25. EXERCISING ACCESS, DATA PORTABILITY, AND DELETION RIGHTS
          </h2>
          <p>
            To exercise the access, data portability, and deletion rights
            described above, please submit a verifiable consumer request to us
            by:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Emailing us with the contact information described below under
              CONTACT US.
            </li>
          </ul>
          <p>
            Only you, or a person registered with the California Secretary of
            State that you authorize to act on your behalf, may make a
            verifiable consumer request related to your Personal Information.
            You may also make a verifiable consumer request on behalf of your
            minor child.
          </p>
          <p>
            You may only make a verifiable consumer request for access or data
            portability twice within a 12-month period. The verifiable consumer
            request must:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Provide sufficient information that allows us to reasonably verify
              you are the person about whom we collected Personal Information or
              an authorized representative.
            </li>
            <li>
              Describe your request with sufficient detail that allows us to
              properly understand, evaluate, and respond to it.
            </li>
          </ul>
          <p>
            We cannot respond to your request or provide you with Personal
            Information if we cannot verify your identity or authority to make
            the request and confirm the Personal Information relates to you.
          </p>
          <p>
            Making a verifiable consumer request does not require you to create
            an account with us. However, we do consider requests made through
            your password protected account sufficiently verified when the
            request relates to Personal Information associated with that
            specific account.
          </p>
          <p>
            We will only use Personal Information provided in a verifiable
            consumer request to verify the requestor&apos;s identity or
            authority to make the request.
          </p>
          <p>
            For instructions on exercising sale opt-out rights, see PERSONAL
            INFORMATION SALES OPT-OUT AND OPT-IN RIGHTS below.
          </p>
          <h2
            id="response-timing-and-format"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            26. RESPONSE TIMING AND FORMAT
          </h2>
          <p>
            We endeavor to respond to a verifiable consumer request within
            forty-five (45) days of its receipt. If we require more time (up to
            90 days), we will inform you of the reason and extension period in
            writing.
          </p>
          <p>
            If you have an account with us, we will deliver our written response
            to that account. If you do not have an account with us, we will
            deliver our written response by mail or electronically, at your
            option.
          </p>
          <p>
            Any disclosures we provide will only cover the 12-month period
            preceding the verifiable consumer request&apos;s receipt. The
            response we provide will also explain the reasons we cannot comply
            with a request, if applicable. For data portability requests, we
            will select a format to provide your Personal Information that is
            readily usable and should allow you to transmit the information from
            one entity to another entity without hindrance.
          </p>
          <p>
            We do not charge a fee to process or respond to your verifiable
            consumer request unless it is excessive, repetitive, or manifestly
            unfounded. If we determine that the request warrants a fee, we will
            tell you why we made that decision and provide you with a cost
            estimate before completing your request.
          </p>
          <h2
            id="personal-information-sales-opt-out-and-opt-in-rights"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            27. PERSONAL INFORMATION SALES OPT-OUT and OPT-IN RIGHTS
          </h2>
          <p>
            You have the right to direct us to not sell your personal
            information at any time (the “right to opt-out). To exercise the
            right to opt-out, you (or your authorized representative) may submit
            a request to us by contacting us (see our CONTACT INFORMATION
            below).
          </p>
          <p>
            Once you make an opt-out request, we will wait at least twelve (12)
            months before asking you to reauthorize Personal Information sales.
            However, you may change your mind and opt back into Personal
            Information sales at any time by visiting our website and sending us
            a message. We will only use Personal Information provided in an opt-
            out request to review and comply with the request.
          </p>
          <h2
            id="right-to-correct-inaccurate-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            28. RIGHT TO CORRECT INACCURATE INFORMATION
          </h2>
          <p>
            California residents have the right to request that we correct any
            inaccurate personal information we maintain about them. To submit a
            correction request, please contact us using the details in the
            “Contact Us” section below. We may need to verify your identity
            before processing your request.
          </p>
          <h2
            id="limit-the-use-of-sensitive-personal-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            29. LIMIT THE USE OF SENSITIVE PERSONAL INFORMATION
          </h2>
          <p>
            You may request that we limit the use of your sensitive personal
            information to only what is necessary to provide services. This
            includes restricting use of information such as Social Security
            numbers, precise geolocation, and financial account credentials. To
            exercise this right, visit our “Limit the Use of My Sensitive
            Personal Information” page or contact us directly.
          </p>
          <h2
            id="notice-of-financial-incentive"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            30. NOTICE OF FINANCIAL INCENTIVE
          </h2>
          <p>
            ATM.com may offer financial incentives, such as cash payments or
            rewards, in exchange for your personal information. These programs
            are entirely optional and subject to additional terms. The value
            provided is based on your engagement and may reflect the estimated
            value of the data you provide. You can opt in by enrolling and may
            opt out at any time by terminating your participation.
          </p>
          <h2
            id="appeal-rights"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            31. APPEAL RIGHTS
          </h2>
          <p>
            If we deny your request to access, delete, or correct your personal
            information, you have the right to appeal our decision. To appeal,
            please submit a written request to{' '}
            <a
              href="mailto:privacy@atm.com"
              className="text-blueberry-700 underline"
            >
              privacy@atm.com
            </a>{' '}
            with the subject line “Privacy Request Appeal.” We will respond
            within 45 days.
          </p>
          <h2
            id="sharing-for-cross-context-behavioral-advertising"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            32. SHARING FOR CROSS-CONTEXT BEHAVIORAL ADVERTISING
          </h2>
          <p>
            We may share your personal information for cross-context behavioral
            advertising purposes. This includes providing your data to
            third-party partners to serve you targeted ads based on your
            interactions across websites or apps. You may opt out of such
            sharing by clicking “Do Not Share My Personal Information” on our
            website.
          </p>
          <h2
            id="note-for-residents-of-the-european-economic-area-united-kingdom-and-switzerland"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            33. NOTE FOR RESIDENTS OF THE EUROPEAN ECONOMIC AREA, UNITED
            KINGDOM, AND SWITZERLAND
          </h2>
          <p>
            ATM.com’s Services are intended solely for users located in the
            fifty (50) United States and the District of Columbia, as set forth
            in our Terms of Service. The Services are not directed at, marketed
            to, or intended for use by residents of the European Economic Area
            (“EEA”), the United Kingdom (“UK”), or Switzerland, and such
            individuals are not authorized to register for or use the Services.
          </p>
          <p>
            We are aware that the European General Data Protection Regulation
            (GDPR), UK GDPR, and Swiss Federal Act on Data Protection (nFADP)
            establish data protection rights for individuals in those
            jurisdictions. While ATM.com does not accept EEA, UK, or Swiss
            residents as users and does not consider itself subject to those
            frameworks in the ordinary course of its operations, we acknowledge
            these regulations and address them below as a matter of transparency
            and in response to requests from our marketing partners.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            33.1 Geographic Restriction and Inadvertent Access
          </h3>
          <p>
            If you are located in the EEA, UK, or Switzerland and have accessed
            our Services, you have done so contrary to our Terms of Service and
            at your own risk. We do not knowingly collect or process personal
            data from individuals in those jurisdictions. If you believe we have
            inadvertently collected your personal data, please contact us
            immediately at legal@atm.com so that we may take appropriate steps
            to address the matter.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            33.2 Data Subject Rights
          </h3>
          <p>
            Regardless of geographic location, we support the following
            individual rights with respect to personal information we hold about
            you. These rights align with the access, correction, deletion, and
            portability rights described elsewhere in this Privacy Policy:
          </p>
          <ul className="list-disc pl-6">
            <li>
              <span className="font-bold">Right to Access.</span> You may
              request confirmation of whether we hold personal data about you
              and, if so, a copy of that data.
            </li>
            <li>
              <span className="font-bold">Right to Correction.</span> You may
              request that we correct inaccurate personal data we hold about
              you.
            </li>
            <li>
              <span className="font-bold">Right to Deletion.</span> You may
              request that we delete personal data we hold about you, subject to
              applicable legal exceptions.
            </li>
            <li>
              <span className="font-bold">Right to Data Portability.</span> You
              may request a copy of your personal data in a structured, commonly
              used, machine-readable format.
            </li>
            <li>
              <span className="font-bold">Right to Restrict Processing.</span>{' '}
              In certain circumstances, you may request that we limit or pause
              the processing of your personal data (for example, while the
              accuracy of the data is being contested).
            </li>
            <li>
              <span className="font-bold">Right to Object.</span> You may object
              to the processing of your personal data for direct marketing
              purposes at any time. Upon receipt of such a request, we will
              cease processing your data for that purpose without condition.
            </li>
          </ul>
          <p>
            To exercise any of the rights listed above, please contact us at
            legal@atm.com or at the mailing address listed in the Contact Us
            section of this Privacy Policy. We will endeavor to respond to all
            requests within thirty (30) days. We may request that you verify
            your identity before we process your request.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            33.3 Automated Decision-Making
          </h3>
          <p>
            As described in this Privacy Policy, ATM.com uses automated
            processes to evaluate eligibility for ATM Instant Cash Advances.
            This process involves analysis of linked bank account data,
            including transaction history and balance information, and produces
            a decision that may affect your ability to access certain features
            of the Services. You have the right to request a human review of any
            such automated decision by contacting us at{' '}
            <a href="mailto:legal@atm.com" className="underline">
              legal@atm.com
            </a>
            .
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            33.4 International Data Transfers
          </h3>
          <p>
            ATM.com is based in the United States, and all personal data we
            collect is stored and processed in the United States. In the event
            that personal data of EEA, UK, or Swiss residents is inadvertently
            collected, any transfer of that data to the United States would
            occur without the benefit of an adequacy decision. Where required by
            applicable law, we would rely on appropriate safeguards, including
            Standard Contractual Clauses approved by the European Commission or
            equivalent mechanisms, for any such transfers. Certain third-party
            processors engaged by ATM.com, including Prodege, GmbH,
            independently comply with applicable transfer mechanisms including
            the EU-US Data Privacy Framework, as described in their respective
            privacy policies referenced elsewhere in this Policy.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            33.5 Contact and Complaints
          </h3>
          <p>
            If you have questions or concerns regarding this section or the
            handling of your personal data, please contact us at{' '}
            <a href="mailto:legal@atm.com" className="underline">
              legal@atm.com
            </a>
            .
          </p>
          <h2
            id="changes-to-our-privacy-notice"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            34. CHANGES TO OUR PRIVACY NOTICE
          </h2>
          <p>
            We reserve the right to amend this privacy notice at our discretion
            and at any time. When we make changes to this privacy notice, we
            will post the updated notice on the website and update the
            notice&apos;s effective date. YOUR CONTINUED USE OF OUR WEBSITE
            FOLLOWING THE POSTING OF CHANGES CONSTITUTES YOUR ACCEPTANCE OF SUCH
            CHANGES.
          </p>
          <h2
            id="contact-us-california"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            35. CONTACT US
          </h2>
          <p>
            If you have any questions, comments, or concerns regarding this
            Privacy Notice for California Residents, please contact us at{' '}
            <a
              href="mailto:legal@atm.com"
              className="text-blueberry-700 underline"
            >
              legal@atm.com
            </a>{' '}
            or send mail to ATM.com, Inc., P.O. Box 7475, Newport Beach, CA
            92658-7475.
          </p>
        </div>
      </Container>
    </>
  )
}

Index.getLayout = (page: ReactElement) => (
  <MarketingLayout chin="white" mainClassName="-mt-[120px]">
    {page}
  </MarketingLayout>
)

export default Index
